// Librairies
import { differenceInCalendarDays, endOfMonth, endOfWeek, endOfYear, formatISO, min, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays } from 'date-fns';

// Classes
import { dateFilterRange } from 'src/app/components/project-pages/dashboard/filter-summary/filter-summary.component';

// Interfaces
// The landing of a text block: what a count or a sum will reach by the end of the current week, month or year
// (done so far + the forecast of the rest). In widget.design.landing.
export type LandingPeriod = 'week' | 'month' | 'year';

export interface LandingDesign {
  enabled?: boolean;
  period?: LandingPeriod;
}

export interface Landing {
  value: number;
  actual: number;
  lower: number | null;      // null without a forecast (not enough history): the value is then what is done
  upper: number | null;
}

// Variables
// The history the forecast is fitted on: enough weeks for the rhythm of the week.
const HISTORY_DAYS = 120;

// Classes
// The day the landing is computed for: the end of the query's date window when it is past (a day picked on the
// dashboard, a closed site), else now.
export function landingReference(query: any, now: Date = new Date()): Date {
  const filter: any = Object.values(query?.filters ?? {}).find((f: any) => f?.type === 'date');
  const end = filter ? dateFilterRange(filter.options, now).end : null;
  return end && end < now ? end : now;
}

export function periodBounds(period: LandingPeriod, now: Date): { start: Date; end: Date } {
  switch (period) {
    case 'week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'year': return { start: startOfYear(now), end: endOfYear(now) };
    default: return { start: startOfMonth(now), end: endOfMonth(now) };
  }
}

// The block's query as a daily series with its forecast: the same collection, filters and value, its date filter
// (or the creation date) over the history up to the reference day, projected to the end of the period. Without
// comparison.
export function landingQuery(query: any, period: LandingPeriod, now: Date = new Date()): any {
  const { compare, forecast, ...rest } = query ?? {};
  const filters = { ...rest.filters };
  const key = Object.keys(filters).find(name => filters[name]?.type === 'date') ?? 'createdDate';
  const reference = landingReference(query, now);
  const { start, end } = periodBounds(period, reference);
  // Enough history for the forecast, and the whole previous period to compare with.
  const from = min([subDays(start, HISTORY_DAYS), periodBounds(period, new Date(start.getTime() - 1)).start]);

  filters[key] = {
    type: 'date',
    options: { dateType: 'Range', min: formatISO(from), eqMin: true, ...(reference < now && { max: formatISO(reference), eqMax: true }) },
  };

  return {
    ...rest,
    filters,
    field: { [key]: { type: 'date', options: { binning: 'day', order: 'ascending' } } },
    forecast: { enabled: true, method: 'auto', horizonDays: differenceInCalendarDays(end, reference) + 1, confidence: 0.9 },
  };
}

// Done since the start of the period (before today), plus the forecast from today (which holds today's part already
// done) to its end; the range adds up the forecast's bounds.
export function landingValue(rows: any[], period: LandingPeriod, now: Date = new Date()): Landing | null {
  if (!rows?.length) return null;

  const { start, end } = periodBounds(period, now);
  const today = startOfDay(now);
  const dayOf = (row: any) => new Date(row.x?.year, row.x?.month ?? 0, row.x?.date ?? 1);
  const within = (row: any, from: Date, to: Date) => dayOf(row) >= from && dayOf(row) <= to;
  const value = (row: any) => Number(row.y) || 0;
  const history = rows.filter(row => row.kind !== 'forecast');
  const forecast = rows.filter(row => row.kind === 'forecast' && within(row, today, end));

  if (!forecast.length) {
    const actual = history.filter(row => within(row, start, end)).reduce((sum, row) => sum + value(row), 0);
    return { value: actual, actual, lower: null, upper: null };
  }

  const actual = history.filter(row => dayOf(row) >= start && dayOf(row) < today).reduce((sum, row) => sum + value(row), 0);
  const sum = (key: 'y' | 'lower' | 'upper') => forecast.reduce((total, row) => total + (Number(row[key]) || 0), 0);

  return { value: actual + sum('y'), actual, lower: actual + sum('lower'), upper: actual + sum('upper') };
}

// The whole period before the current one (last month for a monthly landing): its total, from the history the
// landing query already holds, to compare the landing with.
export function previousTotal(rows: any[], period: LandingPeriod, now: Date = new Date()): { total: number; start: Date; end: Date } {
  const { start, end } = periodBounds(period, new Date(periodBounds(period, now).start.getTime() - 1));
  const total = (rows ?? [])
    .filter(row => row.kind !== 'forecast')
    .filter(row => {
      const day = new Date(row.x?.year, row.x?.month ?? 0, row.x?.date ?? 1);
      return day >= start && day <= end;
    })
    .reduce((sum, row) => sum + (Number(row.y) || 0), 0);

  return { total, start, end };
}
